import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Edit2, Phone, Mail, MapPin, Calendar, FileText, MessageCircle, Plus, Trash2, Users as UsersIcon, Clock, Paperclip, CalendarDays, Tag, UserCheck, Building2, Wallet } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "./StaffDashboard";
import { TeamBadge } from "./ClientsList";
import ClientFormModal from "./ClientFormModal";
import ClientTasksTab from "./ClientTasksTab";
import ClientBudgetTab from "./ClientBudgetTab";
import AttachmentManager from "./AttachmentManager";

const NOTE_TYPES = [
  { value: "general", label: "Note", icon: FileText },
  { value: "call", label: "Phone call", icon: Phone },
  { value: "meeting", label: "Meeting", icon: UsersIcon },
  { value: "email", label: "Email", icon: Mail },
  { value: "visit", label: "Home visit", icon: MapPin },
];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [noteForm, setNoteForm] = useState({ type: "general", content: "" });
  const [posting, setPosting] = useState(false);
  const [tab, setTab] = useState("notes");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, n] = await Promise.all([
        axios.get(`${API}/clients/${id}`),
        axios.get(`${API}/clients/${id}/notes`),
      ]);
      setClient(c.data);
      setNotes(n.data || []);
    } catch {
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteForm.content.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/clients/${id}/notes`, noteForm);
      setNoteForm({ type: "general", content: "" });
      await load();
    } finally {
      setPosting(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    await axios.delete(`${API}/notes/${noteId}`);
    await load();
  };

  const deleteClient = async () => {
    if (!window.confirm("Permanently delete this client and all case notes?")) return;
    await axios.delete(`${API}/clients/${id}`);
    navigate("/staff/clients");
  };

  if (loading) return <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  if (!client) return (
    <div className="text-center py-20">
      <h2 className="font-display text-2xl text-[#1F2A2E] mb-3">Client not found</h2>
      <Link to="/staff/clients" className="btn-primary">Back to clients</Link>
    </div>
  );

  const isEvent = client.team === "community";
  const displayName = isEvent
    ? client.first_name
    : `${client.first_name} ${client.last_name}`.trim();

  return (
    <div data-testid="client-detail-page">
      <Link to="/staff/clients" className="inline-flex items-center gap-2 text-sm text-[#284952] hover:text-[#E2718A] mb-6 transition">
        <ArrowLeft className="w-4 h-4" /> Back to {isEvent ? "events" : "clients"}
      </Link>

      <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#1F2A2E]">{displayName}</h1>
            {isEvent && client.last_name && client.last_name !== "Event" && (
              <div className="text-sm text-[#6B7A80] mt-1">{client.last_name}</div>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <TeamBadge team={client.team || "ndis"} />
              <StatusBadge status={client.status} />
              {client.source === "referral" && <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-[#FCEADB] text-[#A0521A]">From referral</span>}
              {client.assigned_to_name && <span className="text-xs text-[#6B7A80]">{isEvent ? "Lead" : "Assigned to"} {client.assigned_to_name}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowEdit(true)} className="btn-outline text-sm" data-testid="edit-client-btn">
              <Edit2 className="w-4 h-4" /> Edit
            </button>
            {user?.role === "admin" && (
              <button onClick={deleteClient} className="text-sm flex items-center gap-2 px-5 py-2.5 rounded-full text-red-600 border-2 border-red-200 hover:bg-red-50 transition font-semibold" data-testid="delete-client-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </div>

        {isEvent ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 mt-6" data-testid="event-info">
            <InfoRow icon={CalendarDays} label="Event date" value={formatEventDate(client.event_date)} />
            <InfoRow icon={Tag} label="Event type" value={client.event_type} />
            <InfoRow icon={MapPin} label="Location" value={client.event_location} />
            <InfoRow icon={UsersIcon} label="Expected attendees" value={client.expected_attendees ? String(client.expected_attendees) : ""} />
            <InfoRow icon={UserCheck} label="Organiser" value={client.organiser_name} />
            <InfoRow icon={Phone} label="Organiser contact" value={client.organiser_contact} />
            <InfoRow icon={Building2} label="Funded by" value={formatFunder(client.billing_recipient, client.billing_recipient_name)} />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 mt-6">
            <InfoRow icon={Phone} label="Phone" value={client.phone} />
            <InfoRow icon={Mail} label="Email" value={client.email} />
            <InfoRow icon={MapPin} label="Address" value={client.address} />
            <InfoRow icon={Calendar} label="Date of birth" value={client.date_of_birth} />
            <InfoRow label="Gender" value={client.gender} />
            <InfoRow label="Emergency contact" value={client.emergency_contact} />
            <InfoRow label="NDIS number" value={client.ndis_number} mono />
            <InfoRow label="Funding" value={formatFunding(client.funding_type)} />
            <InfoRow label="Plan dates" value={client.plan_start_date && client.plan_end_date ? `${client.plan_start_date} to ${client.plan_end_date}` : ""} />
            <InfoRow label="Primary disability" value={client.primary_disability} />
          </div>
        )}

        {isEvent && client.partner_orgs && (
          <div className="mt-6 pt-6 border-t border-[#F0E5DE]" data-testid="event-partners">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Partner organisations</div>
            <p className="text-[#284952] leading-relaxed whitespace-pre-line">{client.partner_orgs}</p>
          </div>
        )}

        {client.goals && (
          <div className="mt-6 pt-6 border-t border-[#F0E5DE]">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">{isEvent ? "Event goals / outcomes" : "Goals"}</div>
            <p className="text-[#284952] leading-relaxed whitespace-pre-line">{client.goals}</p>
          </div>
        )}

        {!isEvent && client.services?.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Services</div>
            <div className="flex flex-wrap gap-2">
              {client.services.map((s) => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#F1E4D5] border border-[#F0E5DE] text-[#284952]">{s}</span>
              ))}
            </div>
          </div>
        )}

        {client.notes && (
          <div className="mt-6 pt-6 border-t border-[#F0E5DE]">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Summary</div>
            <p className="text-[#284952] leading-relaxed whitespace-pre-line">{client.notes}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 bg-white rounded-full p-1 border border-[#F0E5DE] w-fit flex-wrap" data-testid="client-tabs">
        <TabButton active={tab === "notes"} onClick={() => setTab("notes")} icon={MessageCircle} label="Case notes" testId="tab-notes" />
        <TabButton active={tab === "tasks"} onClick={() => setTab("tasks")} icon={Clock} label="Billable tasks" testId="tab-tasks" />
        {!isEvent && (
          <TabButton active={tab === "budget"} onClick={() => setTab("budget")} icon={Wallet} label="Budget" testId="tab-budget" />
        )}
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")} icon={Paperclip} label="Documents" testId="tab-documents" />
      </div>

      {tab === "tasks" ? (
        <ClientTasksTab clientId={id} />
      ) : tab === "budget" && !isEvent ? (
        <ClientBudgetTab clientId={id} />
      ) : tab === "documents" ? (
        <AttachmentManager
          scope="client"
          parentId={id}
          title={isEvent ? "Event documents" : "Client documents"}
          subtitle={isEvent
            ? "Run sheets, risk assessments, partner agreements, photos, post-event reports."
            : "NDIS plans, service agreements, intake forms, assessments, reports."}
        />
      ) : (
      /* Case notes */
      <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7" data-testid="case-notes-section">
        <h2 className="font-display text-2xl text-[#1F2A2E] mb-5 flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-[#E2718A]" /> Case notes
        </h2>

        <form onSubmit={addNote} className="bg-[#F1E4D5] rounded-2xl p-5 mb-7 space-y-3" data-testid="note-form">
          <div className="flex flex-wrap gap-2">
            {NOTE_TYPES.map((t) => (
              <label key={t.value} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition ${noteForm.type === t.value ? "bg-[#E2718A] text-white" : "bg-white text-[#284952] border border-[#F0E5DE]"}`}>
                <input type="radio" name="note-type" value={t.value} checked={noteForm.type === t.value} onChange={() => setNoteForm((f) => ({ ...f, type: t.value }))} className="hidden" />
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </label>
            ))}
          </div>
          <textarea
            rows={3}
            value={noteForm.content}
            onChange={(e) => setNoteForm((f) => ({ ...f, content: e.target.value }))}
            placeholder="Log a call, meeting, update..."
            className="input-navica"
            data-testid="note-content"
          />
          <button type="submit" disabled={posting || !noteForm.content.trim()} className="btn-primary text-sm" data-testid="add-note-btn">
            <Plus className="w-4 h-4" /> {posting ? "Adding..." : "Add note"}
          </button>
        </form>

        {notes.length === 0 ? (
          <div className="text-center py-10 text-[#6B7A80] text-sm">No notes yet. Log the first one above.</div>
        ) : (
          <ol className="relative border-l-2 border-[#F0E5DE] ml-3 space-y-6">
            {notes.map((n) => {
              const meta = NOTE_TYPES.find((t) => t.value === n.type) || NOTE_TYPES[0];
              const Icon = meta.icon;
              return (
                <li key={n.id} className="ml-6 relative" data-testid={`note-${n.id}`}>
                  <span className="absolute -left-[34px] flex items-center justify-center w-6 h-6 bg-[#E2718A] rounded-full ring-4 ring-white">
                    <Icon className="w-3 h-3 text-white" />
                  </span>
                  <div className="bg-[#F1E4D5] rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#EA823A]">{meta.label}</span>
                        <span className="text-xs text-[#6B7A80] ml-3">by {n.author_name} · {new Date(n.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
                      </div>
                      {(user?.id === n.author_id || user?.role === "admin") && (
                        <button onClick={() => deleteNote(n.id)} className="text-xs text-red-500 hover:text-red-700 transition" data-testid={`delete-note-${n.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-[#284952] leading-relaxed whitespace-pre-line text-sm">{n.content}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      )}

      {showEdit && (
        <ClientFormModal client={client} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${active ? "bg-[#E2718A] text-white" : "text-[#284952] hover:bg-[#F1E4D5]"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function InfoRow({ icon: Icon, label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-sm text-[#1F2A2E] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function formatFunding(v) {
  return { self: "Self managed", plan: "Plan managed", agency: "NDIA managed" }[v] || "";
}

function formatEventDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatFunder(type, name) {
  if (!type) return "";
  const label = { ndia: "NDIA Community Connection", other: "Grant body / sponsor" }[type] || type;
  return name ? `${label}, ${name}` : label;
}
